import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  FileText, 
  Upload, 
  History, 
  Settings, 
  Users, 
  BarChart3, 
  LogOut, 
  User, 
  Menu,
  Shield,
  Home,
  Wrench,
  BookOpen
} from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navLinks = isAdmin ? [
    { to: '/admin', label: 'Dashboard', icon: BarChart3 },
    { to: '/admin/users', label: 'Users', icon: Users },
    { to: '/admin/submissions', label: 'Submissions', icon: FileText },
    { to: '/admin/settings', label: 'Settings', icon: Settings },
    { to: '/admin/maintenance', label: 'Maintenance', icon: Wrench },
    { to: '/admin/guide', label: 'Admin Guide', icon: BookOpen },
  ] : [
    { to: '/dashboard', label: 'Dashboard', icon: Home },
    { to: '/upload', label: 'Upload', icon: Upload },
    { to: '/history', label: 'History', icon: History },
    { to: '/account', label: 'Account', icon: User },
    { to: '/guide', label: 'User Guide', icon: BookOpen },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-3">
              <img 
                src="/images/rba-icon.png" 
                alt="Riverbank Academy Logo" 
                className="h-10 w-auto"
              />
              <div className="hidden sm:block">
                <h1 className="font-serif text-lg font-semibold text-foreground leading-tight">Riverbank Academy</h1>
                <p className="text-xs text-muted-foreground">Integrity Checker</p>
              </div>
            </Link>
            
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.to;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-primary text-primary-foreground' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                    <User className="w-4 h-4 text-accent-foreground" />
                  </div>
                  <span className="hidden sm:block text-sm font-medium">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.full_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground capitalize mt-1">
                    {user?.role} • {user?.student_id}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export const AuthLayout = ({ children, title, subtitle }) => {
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1660128357991-713518efae48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxzdHVkZW50cyUyMHN0dWR5aW5nJTIwbGlicmFyeSUyMHNlcmlvdXN8ZW58MHx8fHwxNzY2MTI4NDU5fDA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-serif text-4xl font-bold mb-4">Academic Integrity Matters</h2>
          <p className="text-lg opacity-90 max-w-md">
            Ensure the originality of your academic work with Riverbank Academy's comprehensive plagiarism detection system.
          </p>
        </div>
      </div>
      
      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/images/rba-icon.png" 
              alt="Riverbank Academy Logo" 
              className="h-16 w-auto"
            />
            <div>
              <h1 className="font-serif text-2xl font-bold text-foreground">Riverbank Academy</h1>
              <p className="text-sm text-muted-foreground">"Empowering Life"</p>
            </div>
          </div>
          
          <div className="mb-8">
            <h2 className="font-serif text-3xl font-bold text-foreground">{title}</h2>
            {subtitle && <p className="text-muted-foreground mt-2">{subtitle}</p>}
          </div>
          
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
